import graphql from 'babel-plugin-relay/macro'
import jwt from 'jsonwebtoken'
import React, { Component } from 'react'
import { withTranslation } from 'react-i18next'
import { createFragmentContainer } from 'react-relay'
import styled from 'styled-components'

import {
  authenticateWithParticipantShortId,
  markMessageAsReadWithFetch
} from '../../api'
import socket from '../../socket'
import { ChatInput } from './ChatInput'
import { ConversationHeader } from './ConversationHeader'
import { Messages } from './Messages'
import { SignUpInput } from './SignUpInput'

const Container = styled.div`
  flex: 1;

  display: flex;
  flex-direction: column;
  align-self: center;
  background-color: white;
  width: 100%;
  height: 100%;
`

class Conversation extends Component {
  constructor (props) {
    super(props)

    const {
      conversation: { displayTitle },
      participant
    } = props

    document.title = `${displayTitle} - Familio`

    this.state = {
      signUpRequired: participant && participant.associationType !== 'USER',
      signedIn: false,
      userId:
        participant &&
        participant.associationType === 'USER' &&
        participant.associationId
    }
  }

  async componentDidMount () {
    const {
      conversation: { id },
      participant
    } = this.props

    if (
      participant &&
      participant.associationType === 'USER' &&
      participant.shortId
    ) {
      const {
        authenticateWithParticipantShortId: { jwt: token }
      } = await authenticateWithParticipantShortId(participant.shortId)
      if (!token) return

      window.familio = {
        ...window.familio,
        jwt: token
      }

      this.setState({ signedIn: true }, () => {
        const { userId } = jwt.decode(token)
        socket.emit('presence', { userId, presence: 'active' })
      })
    }

    socket.on('messageCreated', this.onSocketMessageCreated)
    socket.emit('conversationOpened', { conversationId: id })
  }

  componentWillUnmount () {
    const { conversation } = this.props
    const { userId } = this.state

    socket.removeListener('messageCreated', this.onSocketMessageCreated)
    socket.emit('conversationClosed', { conversationId: conversation.id })
    socket.emit('presence', { userId, presence: 'background' })
  }

  onSocketMessageCreated = async ({ userId, conversationId, id, body }) => {
    console.log('onSocketMessageCreated', userId, conversationId, id, body)
    const { conversation, participant } = this.props

    if (conversationId !== conversation.id) return // only for specific conversation
    if (participant && userId === participant.associationId) return // do not handle own user messages

    await markMessageAsReadWithFetch({
      messageId: id,
      userId:
        participant &&
        participant.associationType === 'USER' &&
        participant.associationId,
      conversationId
    })
  }

  onUserCreated = token => {
    window.familio = {
      jwt: token
    }
    const { userId } = jwt.decode(token)
    const { onRefreshRequired } = this.props

    this.setState({ signUpRequired: false, userId })

    onRefreshRequired && onRefreshRequired()
  }

  renderInputControls = () => {
    const { conversation, participant } = this.props
    const { signUpRequired, userId } = this.state

    if (
      signUpRequired &&
      participant &&
      participant.associationType !== 'USER'
    ) {
      return (
        <SignUpInput
          participant={participant}
          onUserCreated={this.onUserCreated}
        />
      )
    }

    const handleMessageCreated = () =>
      window.messagesRef &&
      window.messagesRef.scrollToEndOfMessages &&
      window.messagesRef.scrollToEndOfMessages()

    if (userId) {
      return (
        <ChatInput
          conversationId={conversation.id}
          userId={userId}
          participant={participant}
          onMessageCreated={handleMessageCreated}
        />
      )
    }

    return (
      <div />
    )
  }

  render () {
    const { conversation, participant } = this.props
    const { signedIn, userId } = this.state

    // wait for sign in if we have an authenticated user accessing chat
    if (participant && participant.associationType === 'USER' && !signedIn) {
      return (
        <Container>
          <ConversationHeader conversation={conversation} />
        </Container>
      )
    }

    return (
      <Container>
        <ConversationHeader conversation={conversation} joinable={!signedIn} />
        <Messages
          ref={ref => (window.messagesRef = ref)}
          viewer={conversation}
          userId={userId}
        />
        {this.renderInputControls()}
      </Container>
    )
  }
}

const ConversationContainer = createFragmentContainer(Conversation, {
  conversation: graphql`
    fragment Conversation_conversation on Conversation {
      id
      displayTitle
      type
      shortId
      ...ConversationHeader_conversation
      ...Messages_viewer
    }
  `,
  participant: graphql`
    fragment Conversation_participant on Participant {
      shortId
      associationId
      associationType
      ...SignUpInput_participant
      ...ChatInput_participant
    }
  `
})

const TranslatedConversationContainer = withTranslation()(ConversationContainer)
export { TranslatedConversationContainer as Conversation }
